import React, { useEffect, useState } from "react";
import {
  Routes,
  Route,
  Navigate,
  useLocation,
  useNavigate,
} from "react-router-dom";
import { Box, Button, UnstyledButton } from "@mantine/core";
import { IconPlus } from "@tabler/icons-react";
import usePermissions from "../../hooks/usePermissions";
import { useFeatureAccess } from "../../context/FeatureAccessContext";
import { useAppointmentTags } from "../../hooks/useAppointmentTags";
import { AppointmentTag } from "../../types/appointmentTags";
import { CashDrawerPage } from "../cashDrawer/CashDrawerPage";
import { isSalesTabEnabled } from "../../utils/salesTabFlag";
import { isGiftCardsEnabled } from "../../utils/giftCardsFlag";
import {
  getVisibleSalesTabs,
  SALES_BASE,
  salesTabRoute,
  SalesTabPath,
} from "../../utils/salesAccess";
import QuickSaleModal from "../calendar/CalendarView/components/QuickSaleModal";
import TransactionsSubTab from "./TransactionsSubTab";
import PurchasesSubTab from "./PurchasesSubTab";
import GiftCardsSubTab from "./GiftCardsSubTab";
import { defaultFilters, pruneTagIds, SalesFilters } from "./shared/filters";
import { SalesDrawerSlotContext } from "./shared/salesDrawerSlot";
import { DateRangePreset } from "../../types/reports";
import styles from "./shared/sales.module.css";

/** "No status filter" sentinel — same for both screens today. */
const DEFAULT_STATUS = "all";

/**
 * Sales tab shell. Owns the page header (title + New Sale) and the sub-tab bar
 * (Transactions · Payments · Cash Drawer · Gift Cards); the content routes
 * render beneath. The global nav/rail stays as-is (the design's "rail" maps to
 * the existing app nav — not rebuilt here). Behind REACT_APP_SALES_TAB.
 *
 * Only the sub-tabs the user's permissions open are shown or routable
 * (utils/salesAccess); a URL for any other tab redirects to the first open
 * one, so the page never requests data the API would refuse with a 403.
 *
 * Every in-page navigation is built from the absolute SALES_BASE so links
 * resolve the same regardless of the current URL — matching the sidebar
 * (NavBar) and avoiding the recursive path growth that relative navigation
 * caused inside the `sales/*` splat route.
 */
function SalesPage() {
  const perms = usePermissions();
  const { access, error: accessError } = useFeatureAccess();
  const location = useLocation();
  const navigate = useNavigate();

  const [quickSaleOpen, setQuickSaleOpen] = useState(false);
  // Bumped after a New Sale completes so the active sub-tab refetches.
  const [refreshNonce, setRefreshNonce] = useState(0);
  // Full-page-height right column the sub-tabs portal their detail drawer into,
  // so it spans the whole page (beside the header) and scrolls independently of
  // the list. `useState` (not a ref) so the portal re-renders once it mounts.
  const [drawerSlot, setDrawerSlot] = useState<HTMLDivElement | null>(null);

  // Filter + search state lifted here so it survives tab switches (each sub-tab
  // unmounts on navigation). Resets only on a full page load, per product.
  const [txFilters, setTxFilters] = useState<SalesFilters>(() =>
    defaultFilters(DEFAULT_STATUS),
  );
  const [txQuery, setTxQuery] = useState("");
  const [txDatePreset, setTxDatePreset] =
    useState<DateRangePreset>("last_30_days");
  const [txCustomStart, setTxCustomStart] = useState<string | null>(null);
  const [txCustomEnd, setTxCustomEnd] = useState<string | null>(null);
  const [puFilters, setPuFilters] = useState<SalesFilters>(() =>
    defaultFilters(DEFAULT_STATUS),
  );
  const [puQuery, setPuQuery] = useState("");
  // Payments tab uses the reports-style date range (presets + custom range).
  const [puDatePreset, setPuDatePreset] =
    useState<DateRangePreset>("last_30_days");
  const [puCustomStart, setPuCustomStart] = useState<string | null>(null);
  const [puCustomEnd, setPuCustomEnd] = useState<string | null>(null);

  // The location's appointment-tag catalog for the Tags filter, loaded once
  // per page mount / location (any staff may read it). On failure the filter
  // simply stays hidden. Selected ids that left the catalog (deleted tag,
  // location switch) are dropped so no filter matches nothing invisibly.
  const { listTags } = useAppointmentTags();
  const [tagOptions, setTagOptions] = useState<AppointmentTag[]>([]);
  useEffect(() => {
    let cancelled = false;
    listTags()
      .then((tags) => {
        if (cancelled) return;
        setTagOptions(tags);
        setTxFilters((f) => pruneTagIds(f, tags));
        setPuFilters((f) => pruneTagIds(f, tags));
      })
      .catch((err) => {
        console.error("Error loading appointment tags:", err);
        if (!cancelled) setTagOptions([]);
      });
    return () => {
      cancelled = true;
    };
  }, [listTags]);

  if (!isSalesTabEnabled()) {
    return <Navigate to="/admin/calendar" replace />;
  }

  // Permissions are fail-closed, so wait for the first /access response (or
  // its error) before deciding which tabs exist. Not `isLoading`: it starts
  // false before the effect-driven fetch kicks off (see SalonContext
  // `accessSettled`), so a hard reload on a Sales URL would bounce to the
  // calendar. A background refresh keeps `access` set, so this doesn't
  // unmount the open tab.
  if (access == null && accessError == null) {
    return null;
  }

  const tabs = getVisibleSalesTabs(perms.permissions, {
    giftCardsEnabled: isGiftCardsEnabled(),
  });
  // Nothing here the user can open (the nav entry is hidden too).
  if (tabs.length === 0) {
    return <Navigate to="/admin/calendar" replace />;
  }

  const landingRoute = salesTabRoute(tabs[0]);
  const isTabOpen = (path: SalesTabPath) => tabs.some((t) => t.path === path);
  // A tab's content when the user can open it, else back to the landing tab.
  const tabElement = (path: SalesTabPath, element: React.ReactElement) =>
    isTabOpen(path) ? element : <Navigate to={landingRoute} replace />;

  // Which sub-tab owns the current URL: the first path segment beneath
  // SALES_BASE. Anchoring at the base (vs. a loose `includes`) keeps the right
  // tab highlighted and mirrors how the routes below are declared.
  const activeSubPath = location.pathname
    .replace(SALES_BASE, "")
    .split("/")
    .filter(Boolean)[0];
  const activeTab =
    tabs.find((t) => t.path === activeSubPath)?.path ?? tabs[0].path;

  return (
    <SalesDrawerSlotContext.Provider value={drawerSlot}>
      <Box className={styles.salesRoot}>
        <Box className={styles.salesMain}>
          <Box className={styles.pageHeader}>
            <Box className={styles.pageHeaderRow}>
              <Box component="h1" className={styles.pageTitle} m={0}>
                Sales
              </Box>
              <Box style={{ flex: 1 }} />
              <Button
                color="var(--brand-teal)"
                radius={8}
                leftSection={<IconPlus size={14} stroke={2.4} />}
                onClick={() => setQuickSaleOpen(true)}
              >
                New Sale
              </Button>
            </Box>
            <Box className={styles.tabBar}>
              {tabs.map((t) => (
                <UnstyledButton
                  key={t.path}
                  className={`${styles.tab} ${
                    activeTab === t.path ? styles.tabActive : ""
                  }`}
                  onClick={() => navigate(salesTabRoute(t))}
                >
                  {t.label}
                </UnstyledButton>
              ))}
            </Box>
          </Box>

          <Box className={styles.contentPane}>
            <Routes>
              <Route
                path="/"
                element={<Navigate to={landingRoute} replace />}
              />
              <Route
                path="transactions"
                element={tabElement(
                  "transactions",
                  <TransactionsSubTab
                    refreshNonce={refreshNonce}
                    filters={txFilters}
                    onFiltersChange={setTxFilters}
                    query={txQuery}
                    onQueryChange={setTxQuery}
                    datePreset={txDatePreset}
                    customStart={txCustomStart}
                    customEnd={txCustomEnd}
                    tagOptions={tagOptions}
                    onDateChange={(preset, start, end) => {
                      setTxDatePreset(preset);
                      setTxCustomStart(start);
                      setTxCustomEnd(end);
                    }}
                  />,
                )}
              />
              <Route
                path="purchases"
                element={tabElement(
                  "purchases",
                  <PurchasesSubTab
                    refreshNonce={refreshNonce}
                    filters={puFilters}
                    onFiltersChange={setPuFilters}
                    query={puQuery}
                    onQueryChange={setPuQuery}
                    datePreset={puDatePreset}
                    customStart={puCustomStart}
                    customEnd={puCustomEnd}
                    tagOptions={tagOptions}
                    onDateChange={(preset, start, end) => {
                      setPuDatePreset(preset);
                      setPuCustomStart(start);
                      setPuCustomEnd(end);
                    }}
                  />,
                )}
              />
              <Route
                path="cash-drawer"
                element={tabElement("cash-drawer", <CashDrawerPage />)}
              />
              <Route
                path="gift-cards"
                element={tabElement(
                  "gift-cards",
                  <GiftCardsSubTab refreshNonce={refreshNonce} />,
                )}
              />
              {/* Unknown sub-path → the landing tab. */}
              <Route
                path="*"
                element={<Navigate to={landingRoute} replace />}
              />
            </Routes>
          </Box>
        </Box>

        {/* Full-page-height drawer host. Empty (zero-width) until a sub-tab
            portals its detail drawer in; then it's a sticky right rail that
            scrolls independently of the list. */}
        <Box ref={setDrawerSlot} className={styles.drawerColumn} />
      </Box>

      <QuickSaleModal
        opened={quickSaleOpen}
        onClose={() => setQuickSaleOpen(false)}
        onQuickSaleComplete={() => {
          setQuickSaleOpen(false);
          setRefreshNonce((n) => n + 1);
          // Transactions when the user can open it (it leads the tab order).
          navigate(landingRoute);
        }}
      />
    </SalesDrawerSlotContext.Provider>
  );
}

export default SalesPage;
